import type {
  DemoAccessAssignment,
  GrantDemoAccessRequest,
} from "@/store/api/baseApi";

export type DemoAccessIdentifierType = "email" | "firebaseId";

export interface DemoAccessRevokeTarget {
  firebaseId: string;
  displayName: string;
}

export interface DemoAccessSummary {
  activeAssignments: number;
  uniqueGrantors: number;
  lastUpdatedAt: string | null;
}

const normaliseString = (value: string | null | undefined) =>
  value?.trim() ?? "";

export const buildDemoAccessSummary = (
  assignments: DemoAccessAssignment[],
): DemoAccessSummary => {
  const uniqueGrantors = new Set<string>();
  let lastUpdatedAt: string | null = null;

  for (const assignment of assignments) {
    const grantedBy = normaliseString(assignment.grantedBy);
    if (grantedBy.length > 0) {
      uniqueGrantors.add(grantedBy);
    }

    const updatedAt = normaliseString(assignment.updatedAt);
    if (updatedAt.length === 0) {
      continue;
    }

    if (lastUpdatedAt === null || updatedAt > lastUpdatedAt) {
      lastUpdatedAt = updatedAt;
    }
  }

  return {
    activeAssignments: assignments.filter(
      (assignment) => assignment.demoAccessEnabled,
    ).length,
    uniqueGrantors: uniqueGrantors.size,
    lastUpdatedAt,
  };
};

export const sortDemoAccessAssignments = (
  assignments: DemoAccessAssignment[],
): DemoAccessAssignment[] => {
  return [...assignments].sort((left, right) => {
    if (left.demoAccessEnabled !== right.demoAccessEnabled) {
      return left.demoAccessEnabled ? -1 : 1;
    }

    const leftName = normaliseString(
      left.displayName || left.email || left.firebaseId,
    );
    const rightName = normaliseString(
      right.displayName || right.email || right.firebaseId,
    );

    return leftName.localeCompare(rightName, undefined, {
      sensitivity: "base",
    });
  });
};

export const filterDemoAccessAssignments = (
  assignments: DemoAccessAssignment[],
  options?: {
    includeDisabledAssignments?: boolean;
  },
): DemoAccessAssignment[] => {
  if (options?.includeDisabledAssignments) {
    return assignments;
  }

  return assignments.filter((assignment) => assignment.demoAccessEnabled);
};

export const buildDemoAccessGrantPayload = (args: {
  identifierType: DemoAccessIdentifierType;
  userIdentifier: string;
}): GrantDemoAccessRequest | null => {
  const trimmedIdentifier = args.userIdentifier.trim();
  if (trimmedIdentifier.length === 0) {
    return null;
  }

  return args.identifierType === "email"
    ? { email: trimmedIdentifier }
    : { firebaseId: trimmedIdentifier };
};

export const buildDemoAccessRevokeTarget = (
  assignment: DemoAccessAssignment,
): DemoAccessRevokeTarget => ({
  firebaseId: assignment.firebaseId,
  displayName:
    normaliseString(assignment.displayName) ||
    normaliseString(assignment.email) ||
    assignment.firebaseId,
});

export const buildDemoAccessRevokeAriaLabel = (target: {
  displayName?: string | null;
  firebaseId?: string | null;
}) => {
  const contextLabel =
    normaliseString(target.displayName) ||
    normaliseString(target.firebaseId) ||
    "selected user";

  return `Revoke demo access for ${contextLabel}`;
};
